import { onSnapshot, types } from 'mobx-state-tree';
import { createContext, useContext } from 'react';
import { DiggingPermitModel } from './applications/diggingPermitModel';
import { LeaseholdModel } from './applications/leaseholdModel';
import { TAPlanModel } from './applications/taPlanModel';
import { diggingPermitCase, taPlanCase } from './ApplicationsWithTestData';
import { DomainModel, TADomainModel } from './domains/domainModel';
import { MunicipalityModel } from './municipalityModel';
import { SettingsModel } from './settingsModel';
import { UserModel } from './userModel';

export const RootModel = types
  .model('RootModel', {
    settings: SettingsModel,
    user: types.maybe(UserModel),
    domains: types.array(DomainModel),
    taDomains: types.array(TADomainModel),
    municipality: MunicipalityModel,
    applications: types.array(types.union(DiggingPermitModel, LeaseholdModel, TAPlanModel)),
    activeApplication: types.maybe(
      types.late(() => types.reference(types.union(DiggingPermitModel, LeaseholdModel, TAPlanModel)))
    )
  })
  .actions(self => ({
    setActiveApplication: (caseNumber, caseType) => {
      const application = self.applications.find(a => a.caseNumber === caseNumber && a.caseType === caseType);
      if (application) {
        self.activeApplication = application;
      } else {
        if (caseNumber === null) {
          self.activeApplication = undefined;
        } else if (self.applications.length > 0) {
          self.activeApplication = self.applications[0];
        } else {
          self.activeApplication = undefined;
        }
      }
    },
    setDomains: domains => {
      self.domains = domains;
    },
    setTADomains: taDomains => {
      self.taDomains = taDomains;
    },
    getDomainByName: domainName => {
      const chosenDomain = self.domains.find(d => d.domainName === domainName);
      return chosenDomain ? chosenDomain : { codedValues: [] };
    },
    addApplication: app => {
      self.applications.push(app);
      self.setActiveApplication(app.caseNumber, app.caseType);
    },
    removeApplication: tabKey => {
      let caseType = parseInt(tabKey.slice(0, 1));
      let caseNumber = tabKey.slice(1);
      const index = self.applications.findIndex(x => x.caseNumber === caseNumber && x.caseType === caseType);
      const currentApp = self.applications[index];
      const numberOfApplications = self.applications.length;

      if (numberOfApplications > 1 && self.activeApplication?.caseNumber === caseNumber) {
        const newActiveAppIndex = index === 0 ? index + 1 : index - 1;
        self.activeApplication = self.applications[newActiveAppIndex];
      }
      if (self.activeApplication?.caseNumber === caseNumber && self.activeApplication?.caseType === caseType) {
        let lastIndex = index - 1;

        if (lastIndex >= 0) {
          self.activeApplication = self.applications[lastIndex];
        } else {
          self.activeApplication = undefined;
        }
      }

      self.applications = self.applications.filter(a => a.gid !== currentApp.gid);
    },
    removeAllApplications: () => {
      self.applications = [];
      self.activeApplication = undefined;
    },
    setMunicipality: municipality => (self.municipality = municipality),
    setUser: user => (self.user = user)
  }))
  .views(self => ({
    get srid() {
      return self.settings?.mapConfiguration?.baseLayer?.wkid;
    }
  }));

let initialState = RootModel.create({
  activeApplication: undefined,
  applications: [],
  municipality: { tenantId: 'local' },
  settings: {}
});

const data = localStorage.getItem('rootState');
if (data) {
  const json = JSON.parse(data);
  if (json.applications) {
    json.applications.forEach(a => initialState.addApplication(a));
  }
  if (json.activeApplication) {
    initialState.setActiveApplication(json.activeApplication.caseNumber, json.activeApplication.caseType);
  }
}

const diggingPermitTestCase = DiggingPermitModel.create(diggingPermitCase);
export const rootStoreForTests = RootModel.create({
  activeApplication: diggingPermitTestCase,
  applications: [diggingPermitTestCase, TAPlanModel.create(taPlanCase)],
  municipality: { tenantId: 'local' },
  settings: {
    externalApplicationSettings: {
      identification: 'externalApplicationSettings',
      modificationDate: 'today',
      availableApplicationTypes: {
        diggingPermit: true,
        leasehold: true,
        trafficArrangementPlan: true
      },
      textsConfiguration: {
        confirmationTexts: {
          diggingPermit: '*Digging Permit confirmationtext*',
          leasehold: '*Leasehold confirmationtext*',
          taPlan: '*TAPlan confirmationtext*'
        }
      }
    },
    generalSettings: {
      identification: 'GeneralSettings',
      modificationDate: 'today',
      locators: [
        {
          url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer',
          key: 15
        }
      ]
    }
  },
  user: {
    address: 'Adress 1b',
    city: 'Gislaved',
    email: 'name@esri-sgroup.com',
    mobilePhoneNumber: '0000000000',
    name: 'FirstName LastName',
    organisationName: undefined,
    organisationNumber: undefined,
    phoneNumber: '0000000000',
    socialSecurityNumber: '2206012383', //taken from Skatteverket testpersonnr
    zipCode: '11111',
    accountType: 'Personal'
  }
});

export const rootStore = initialState;

onSnapshot(rootStore, snapshot => {
  const rootSnapshot = JSON.parse(JSON.stringify(snapshot));
  rootSnapshot.applications.forEach(application => {
    application.attachments = [];
    application.tracker.tracker = [];
  });
  const minimalSnapshot = {
    applications: rootSnapshot.applications,
    activeApplication: rootSnapshot.activeApplication
  };
  try {
    localStorage.setItem('rootState', JSON.stringify(minimalSnapshot));
  } catch (e) {
    console.log(minimalSnapshot);
    throw e;
  }
});

const RootStoreContext = createContext(null);

export const RootStoreProvider = RootStoreContext.Provider;

export function useRootStore() {
  const store = useContext(RootStoreContext);
  if (store === null) {
    throw new Error('RootStore cannot be null, please add the context provider for RootStore');
  }
  return store;
}
